import { useState, useRef } from 'react'

export const OTP_LENGTH = 6

// The per-digit one-time-code input shared by the sign-in OTP modal and the
// email verification code entry: typing advances focus, backspace on an
// empty box steps back, pasting fills every box, and filling the last box
// calls onComplete(code). Spread getInputProps(index) onto each <input>.
// locked makes the boxes read-only and stops onComplete from firing (e.g.
// once the code has been accepted).
export default function useOtpDigits({ onComplete, locked }) {
  const [digits, setDigits] = useState(Array(OTP_LENGTH).fill(''))
  const inputRefs = useRef([])

  const isComplete = digits.every((d) => d)

  function complete(newDigits) {
    if (!locked) onComplete(newDigits.join(''))
  }

  function handleChange(index, value) {
    const char = value.replace(/\D/g, '').slice(-1)
    const newDigits = [...digits]
    newDigits[index] = char
    setDigits(newDigits)
    if (char && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1].focus()
    } else if (char && index === OTP_LENGTH - 1 && newDigits.every((d) => d)) {
      complete(newDigits)
    }
  }

  function handleKeyDown(index, e) {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1].focus()
    }
  }

  function handlePaste(e) {
    e.preventDefault()
    if (locked) return
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH)
    const newDigits = [...digits]
    pasted.split('').forEach((char, i) => { newDigits[i] = char })
    setDigits(newDigits)
    const nextEmpty = newDigits.findIndex((d) => !d)
    const focusIndex = nextEmpty === -1 ? OTP_LENGTH - 1 : nextEmpty
    inputRefs.current[focusIndex].focus()
    if (nextEmpty === -1) {
      complete(newDigits)
    }
  }

  function reset() {
    setDigits(Array(OTP_LENGTH).fill(''))
    if (inputRefs.current[0]) inputRefs.current[0].focus()
  }

  function getInputProps(index) {
    const digit = digits[index]
    return {
      ref: (el) => { inputRefs.current[index] = el },
      type: 'tel',
      inputMode: 'numeric',
      autoComplete: 'off',
      maxLength: 1,
      value: digit,
      readOnly: locked,
      'data-filled': digit ? 'true' : 'false',
      onChange: (e) => handleChange(index, e.target.value),
      onKeyDown: (e) => handleKeyDown(index, e),
      onFocus: (e) => e.target.select(),
      onPaste: handlePaste,
      autoFocus: index === 0,
    }
  }

  return {
    digits, code: digits.join(''), isComplete, reset, getInputProps,
  }
}
